import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';

type CustomerType = 'registered' | 'guest' | null;

const isCustomerType = (value: unknown): value is CustomerType =>
    value === 'registered' || value === 'guest' || value === null;

/**
 * Returns the customer type for the current user.
 * The difference between this and useCustomerType is that this will wait until the auth process is complete. useCustomerType relies solely on listening to the local storage and emitting events when changed
 * @returns The customer type for the current user after triggering the auth process.
 */
export const useSLTCustomerType = (
    options?: Omit<
        UseQueryOptions<CustomerType, unknown, CustomerType, Array<string>>,
        'queryKey' | 'queryFn'
    >,
): UseQueryResult<CustomerType> => {
    const auth = useAuthContext();
    // eslint-disable-next-line @tanstack/query/prefer-query-object-syntax
    return useQuery(
        ['useSLTCustomerType'],
        () =>
            auth
                .ready()
                .then((authData) =>
                    isCustomerType(authData.customer_type) ? authData.customer_type : null,
                ),
        options,
    );
};
